import React from 'react';
import { motion } from 'framer-motion';
import { useHomepageConfig } from '@/hooks/useHomepageConfig';
import { useEditMode } from '@/context/EditModeContext';
import { Editable } from '@/components/edit/Editable';
import { EditableImage } from '@/components/edit/EditableImage';

const DEFAULT_PILLARS = [
  {
    title: 'Experience',
    image: '/api/media/casa-samba/library/images/pillars/pillar-experience.jpg',
    borderColor: '#E67817',
    text: "Casa Samba has been performing over 36 years at events like Super Bowl parades, festivals, American Express commercial, McDonald's, Mardi Gras balls like the Krewe of Endymion and Zulu balls and hundreds of conventions and special events.",
  },
  {
    title: 'Professionalism',
    image: '/api/media/casa-samba/library/images/pillars/pillar-professionalism.jpg',
    borderColor: '#A8D720',
    text: 'Casa Samba relies on being professional; being on time, commitment to following instructions as well as being customizable to make your event an unforgettable experience.',
  },
  {
    title: 'Unique',
    image: '/api/media/casa-samba/library/images/pillars/pillar-unique.jpg',
    borderColor: '#4FD8E8',
    text: 'Casa Samba is the unique experience alternative \u2014 after they have the New Orleans Mardi Gras experience they can have the experience of being at the Carnival in Rio de Janeiro or Salvador Bahia.',
  },
];

export const HomePillars = () => {
  const cfg = useHomepageConfig();
  const { enabled, getValue } = useEditMode();
  // In edit mode use the draft so edits reflect immediately; otherwise use fetched config
  const source = enabled ? (getValue('pillars') || cfg?.pillars) : cfg?.pillars;
  const items = (Array.isArray(source) && source.length) ? source : DEFAULT_PILLARS;

  return (
    <section className="relative py-20 md:py-24 px-6 bg-black/25 backdrop-blur-md border-y border-white/10" data-testid="pillars-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-3 gap-8 md:gap-10">
          {items.map((p, idx) => (
            <motion.div
              key={p.title + idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.15 }}
              className="flex flex-col"
              data-testid={`pillar-${(p.title || '').toLowerCase()}`}
            >
              <EditableImage
                path={`pillars.${idx}.image`}
                fallback={p.image}
                alt={`${p.title} \u2014 Escola de Samba Casa Samba`}
                className="w-full"
                imgClassName="w-full h-64 md:h-72 object-cover"
              />
              <div className="mt-8 py-1.5 px-4 text-center" style={{ border: `3px solid ${p.borderColor || '#F4E400'}` }}>
                <Editable
                  as="span"
                  path={`pillars.${idx}.title`}
                  fallback={p.title}
                  className="font-bold text-lg tracking-wide uppercase"
                  style={{ color: '#FFCC00' }}
                />
              </div>
              <div className="mt-8 pt-8 border-t border-border/60">
                <Editable
                  as="p"
                  path={`pillars.${idx}.text`}
                  fallback={p.text}
                  className="text-foreground/90 leading-relaxed text-sm md:text-base"
                  multiline
                />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HomePillars;
